/**
 * Batch flip: a photo-level mirror of its active source, independent of crop
 * (Crop's panel edits it today; any tool may). Like the crop it is never baked
 * into the source — preview mirrors in CSS, export in the canvas. The source is
 * flipped first, so a crop rect is drawn over the flipped photo.
 */

/** Normalized (0..1) box, the shape of `CropRect` and `NormalizedBBox`. */
interface Box {
  x: number
  y: number
  width: number
  height: number
}

export interface BatchFlip {
  /** Mirrored left ↔ right (the panel's Flip). */
  horizontal: boolean
  /** Mirrored top ↔ bottom (the panel's Mirror). */
  vertical: boolean
}

export const NO_FLIP: BatchFlip = { horizontal: false, vertical: false }

export function isFlipped(flip: BatchFlip | null | undefined): boolean {
  return Boolean(flip && (flip.horizontal || flip.vertical))
}

/** Same mirror; null is no flip. */
export function batchFlipsEqual(a: BatchFlip | null | undefined, b: BatchFlip | null | undefined): boolean {
  const left = a ?? NO_FLIP
  const right = b ?? NO_FLIP
  return left.horizontal === right.horizontal && left.vertical === right.vertical
}

/** A normalized box of the unflipped photo, where it lands on the flipped one. */
export function flipNormalizedBox<T extends Box>(box: T, flip: BatchFlip | null | undefined): T {
  if (!isFlipped(flip)) return box
  return {
    ...box,
    x: flip!.horizontal ? 1 - box.x - box.width : box.x,
    y: flip!.vertical ? 1 - box.y - box.height : box.y,
  }
}
